/**
 * useAlarmEngine Hook
 *
 * Alarm evaluation engine for the Live Trading phase. Evaluates alarm rules
 * every second against the current wall-clock time (in each rule's resolved
 * timezone), fires toast notifications for qualifying alarms, and plays
 * optional sound tones via the Web Audio API.
 *
 * Lifecycle:
 * - Starts evaluation on mount (when user enters Live Trading)
 * - Stops evaluation on unmount (when user leaves Live Trading)
 * - Pauses on tab hidden (visibilitychange), resumes on visible — no backlog
 *
 * Follows CODING_STANDARDS §2.2 (Polling with React Strict Mode):
 * - Refs synced in render body for stable interval access
 * - Single stable setInterval with empty deps
 * - clearInterval in cleanup
 *
 * @see utils/alarmEvaluation.ts — pure evaluation functions
 * @see utils/alarmSound.ts — Web Audio API tone playback
 * @see types/shared/AlarmRule.ts — AlarmRule discriminated union
 */

import { useEffect, useRef } from 'react';

import { addToast } from '@enfomentis/ui';
import { basicSettingsService } from '@/services/BasicSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { playAlarmTone, disposeAudio } from '@/utils/alarmSound';
import {
  resolveAlarmTimezone,
  getTimePartsInTimezone,
  evaluateAlarmRule,
} from '@/utils/alarmEvaluation';

import type { AlarmRule } from '@/types/shared/AlarmRule';
import type { TimeParts } from '@/utils/alarmEvaluation';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Evaluation interval — 1 second per tick */
const TICK_INTERVAL_MS = 1000;

/** Minimum gap between staggered toast notifications */
const TOAST_STAGGER_MS = 300;

/** SyncCoordinator table name for BasicSettings */
const BASIC_SETTINGS_TABLE = 'basic_settings';

// ---------------------------------------------------------------------------
// Options interface
// ---------------------------------------------------------------------------

/** Configuration for the alarm engine hook */
export interface UseAlarmEngineOptions {
  /** Alarm rules from LiveTradingSettings (synced to ref each render) */
  alarmRules: AlarmRule[];
  /** Global auto-dismiss duration in ms for toast notifications (synced to ref each render) */
  alarmAutoDismissMs: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Alarm evaluation engine hook.
 *
 * Call inside `LiveTradingPhase` — starts evaluation on mount, stops on unmount.
 * Fires toast notifications and plays sounds when alarm rules qualify.
 *
 * @param options - Alarm rules and auto-dismiss duration (reactive via ref sync)
 */
export function useAlarmEngine(options: UseAlarmEngineOptions): void {
  // ---------------------------------------------------------------------------
  // Render-body ref sync (CODING_STANDARDS §2.2)
  // These refs are read inside the setInterval callback. Syncing in the render
  // body (not in effects) ensures the interval always sees current values.
  // ---------------------------------------------------------------------------

  const alarmRulesRef = useRef(options.alarmRules);
  const autoDismissMsRef = useRef(options.alarmAutoDismissMs);

  alarmRulesRef.current = options.alarmRules;
  autoDismissMsRef.current = options.alarmAutoDismissMs;

  // ---------------------------------------------------------------------------
  // Persistent refs (survive across ticks and visibility changes)
  // ---------------------------------------------------------------------------

  /** User's display timezone from BasicSettings — updated on mount + cross-tab sync */
  const displayTimezoneRef = useRef('');

  /** Per-rule dedupe map: ruleId → last trigger key */
  const lastFiredKeysRef = useRef(new Map<string, string>());

  /** Whether the initial timezone fetch has completed (gate for evaluateTick) */
  const timezoneLoadedRef = useRef(false);

  /** Pending stagger setTimeout IDs for cleanup */
  const staggerTimeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // ---------------------------------------------------------------------------
  // Load displayTimezone + subscribe to cross-tab changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let mounted = true;

    /** Fetch displayTimezone from BasicSettings service */
    const loadTimezone = async (): Promise<void> => {
      try {
        const settings = await basicSettingsService.getOrCreateDefaultSettings();
        if (mounted) {
          displayTimezoneRef.current = settings.displayTimezone;
          timezoneLoadedRef.current = true;
        }
      } catch {
        // Non-fatal — resolveAlarmTimezone falls back to BROWSER_TIMEZONE.
        // Still ungate evaluation so alarms fire with browser timezone.
        if (mounted) {
          timezoneLoadedRef.current = true;
        }
      }
    };

    // Initial load
    void loadTimezone();

    // Subscribe to basic_settings changes (cross-tab sync)
    const unsubscribe = syncCoordinatorService.subscribe(
      BASIC_SETTINGS_TABLE,
      () => {
        void loadTimezone();
      },
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Main evaluation engine
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    /**
     * Clear all pending stagger timeouts.
     * Called on pause (tab hidden), unmount, and before each new tick's stagger batch.
     */
    const clearStaggerTimeouts = (): void => {
      for (const id of staggerTimeoutIdsRef.current) {
        clearTimeout(id);
      }
      staggerTimeoutIdsRef.current = [];
    };

    /**
     * Fire a single alarm: show toast + play optional sound.
     */
    const fireAlarm = (rule: AlarmRule, dismissMs: number): void => {
      addToast({
        title: rule.message,
        color: rule.color,
        timeout: dismissMs,
      });

      if (rule.soundId) {
        void playAlarmTone(rule.soundId);
      }
    };

    /**
     * Evaluate all enabled alarm rules against the current wall-clock time.
     * Collects qualifying rules and fires them with staggered timing.
     */
    const evaluateTick = (): void => {
      // Gate: skip evaluation until initial timezone fetch completes
      if (!timezoneLoadedRef.current) return;

      const rules = alarmRulesRef.current;
      const dismissMs = autoDismissMsRef.current;
      const displayTz = displayTimezoneRef.current;
      const lastFiredKeys = lastFiredKeysRef.current;
      const now = new Date();

      // Per-tick timezone → TimeParts cache (each unique TZ computed once)
      const timePartsCache = new Map<string, TimeParts>();

      // Collect rules that should fire this tick
      const toFire: AlarmRule[] = [];

      for (const rule of rules) {
        if (!rule.enabled) continue;

        // Resolve timezone for this rule
        const tz = resolveAlarmTimezone(rule, displayTz);

        // Get or compute time parts for this timezone
        let timeParts = timePartsCache.get(tz);
        if (!timeParts) {
          timeParts = getTimePartsInTimezone(now, tz);
          timePartsCache.set(tz, timeParts);
        }

        // Evaluate
        const lastKey = lastFiredKeys.get(rule.id);
        const result = evaluateAlarmRule(rule, timeParts, lastKey);

        if (result.shouldFire && result.triggerKey !== undefined) {
          lastFiredKeys.set(rule.id, result.triggerKey);
          toFire.push(rule);
        }
      }

      // Fire with stagger (300ms gap between simultaneous alarms)
      if (toFire.length > 0) {
        // Clear any leftover stagger timeouts from a previous tick
        clearStaggerTimeouts();

        toFire.forEach((rule, i) => {
          const delay = i * TOAST_STAGGER_MS;

          if (delay === 0) {
            // First toast fires immediately (no setTimeout overhead)
            fireAlarm(rule, dismissMs);
          } else {
            const timeoutId = setTimeout(() => {
              fireAlarm(rule, dismissMs);
            }, delay);
            staggerTimeoutIdsRef.current.push(timeoutId);
          }
        });
      }
    };

    /**
     * Start the evaluation interval.
     */
    const startInterval = (): void => {
      if (intervalId !== null) return; // Already running
      intervalId = setInterval(evaluateTick, TICK_INTERVAL_MS);
    };

    /**
     * Stop the evaluation interval and clear pending stagger timeouts.
     */
    const stopInterval = (): void => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      clearStaggerTimeouts();
    };

    /**
     * Handle tab visibility changes.
     * Hidden → pause (no evaluation, no backlog).
     * Visible → resume (fresh evaluation from current time).
     */
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        stopInterval();
      } else {
        startInterval();
      }
    };

    // Start immediately
    startInterval();

    // Subscribe to visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void disposeAudio();
    };
  }, []);
}
